import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AdminRoute } from './components/auth/AdminRoute';
import { CodeServerRoute } from './components/auth/CodeServerRoute';
import { GuestRoute } from './components/auth/GuestRoute';
import { OnboardingRoute } from './components/auth/OnboardingRoute';
import { ProjectRoute } from './components/auth/ProjectRoute';
import { NotFoundPage } from './components/errors/ErrorPage';
import { ServersPage } from './pages/ServersPage';
import { ProjectsPage } from './pages/dashboard/ProjectsPage';
import { DeploymentsPage } from './pages/dashboard/DeploymentsPage';
import { BackupsPage } from './pages/dashboard/BackupsPage';
import { TasksPage } from './pages/dashboard/TasksPage';
import { TaskDetailPage } from './pages/dashboard/TaskDetailPage';
import { AuditLogsPage } from './pages/dashboard/AuditLogsPage';
import { ProjectSettingsPage } from './pages/dashboard/ProjectSettingsPage';
import { LoginPage, SignupPage, ForgotPasswordPage, Verify2FAPage, ResendConfirmationPage, ConfirmEmailPage, ResetPasswordPage, ReauthPage, OAuthAccountSelectPage, OAuthLinkConfirmPage, InviteSignupPage, InviteLoginPage, InviteConfirmPage } from './pages/auth';
import { SelectIntentPage, SetupGithubPage } from './pages/onboarding';
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { SharedServersPage } from './pages/admin/SharedServersPage';
import { SharedServerDetailPage } from './pages/admin/SharedServerDetailPage';
import { SharedServerQueuePage } from './pages/admin/SharedServerQueuePage';
import { AdminTasksPage } from './pages/admin/AdminTasksPage';
import { AdminTaskDetailPage } from './pages/admin/AdminTaskDetailPage';
import { AdminB2Page } from './pages/admin/AdminB2Page';
import { AdminTestTagsPage } from './pages/admin/AdminTestTagsPage';
import { AdminOdooVersionsPage } from './pages/admin/AdminOdooVersionsPage';
import { AdminContinuousTestingPage } from './pages/admin/AdminContinuousTestingPage';
import { AdminEmailToolsPage } from './pages/admin/AdminEmailToolsPage';
import { AdminProjectToolsPage } from './pages/admin/AdminProjectToolsPage';
import { AdminPlaybookRunnerPage } from './pages/admin/AdminPlaybookRunnerPage';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import {
  AccountSettingsPage,
  ChangePasswordPage,
  TwoFactorPage,
  LinkedAccountsPage,
  MembershipsPage,
  GitHubSettingsPage,
  SSHKeysPage,
  BillingSettingsPage,
  ApiAccessPage,
  LegalSettingsPage,
} from './pages/settings';
import { DoployAiPage } from './pages/ai/DoployAiPage';
import { KnowledgeBasePage } from './pages/ai/KnowledgeBasePage';
import { InsightsDashboard } from './pages/insights/InsightsDashboard';
import { CallDetailPage } from './pages/insights/CallDetailPage';
import { CustomerDashboard } from './pages/insights/CustomerDashboard';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      refetchOnWindowFocus: true,
    },
  },
});

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();

  // Handle email confirmation from query parameter or direct route
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const confirmCode = urlParams.get('confirm');

    // Check if we're on the direct /confirm/:code route
    const confirmMatch = location.pathname.match(/^\/confirm\/(.+)$/);
    const directCode = confirmMatch ? confirmMatch[1] : null;

    const code = confirmCode || directCode;

    if (code) {
      if (confirmCode) {
        // Remove the query parameter from the URL
        const newUrl = `${location.pathname}${location.hash}`;
        window.history.replaceState({}, '', newUrl);
      }

      // Navigate to the confirmation page with the code
      navigate(`/auth/confirm/${code}`);
    }
  }, [location, navigate]);

  return (
    <Routes>
      {/* Auth Routes (guest only - redirect to /projects if already logged in) */}
      <Route path="/auth/login" element={
        <GuestRoute>
          <LoginPage />
        </GuestRoute>
      } />
      <Route path="/auth/signup" element={
        <GuestRoute>
          <SignupPage />
        </GuestRoute>
      } />
      <Route path="/auth/forgot-password" element={
        <GuestRoute>
          <ForgotPasswordPage />
        </GuestRoute>
      } />
      <Route path="/auth/verify-2fa" element={<Verify2FAPage />} />
      <Route path="/auth/resend-confirmation" element={<ResendConfirmationPage />} />
      <Route path="/auth/confirm/:code" element={<ConfirmEmailPage />} />
      <Route path="/confirm/:code" element={<ConfirmEmailPage />} />
      <Route path="/auth/reset-password/:code" element={<ResetPasswordPage />} />
      <Route path="/auth/reauth" element={
        <ProtectedRoute>
          <ReauthPage />
        </ProtectedRoute>
      } />
      <Route path="/auth/oauth-account-select" element={<OAuthAccountSelectPage />} />
      <Route path="/auth/oauth-link-confirm" element={<OAuthLinkConfirmPage />} />

      {/* Project Invitation Routes */}
      <Route path="/auth/invite/:inviteId/:secret" element={<InviteSignupPage />} />
      <Route path="/auth/invite-login/:inviteId/:secret" element={<InviteLoginPage />} />
      <Route path="/auth/invite-confirm/:inviteId/:secret" element={<InviteConfirmPage />} />

      {/* Legacy Flask routes - redirect to new paths */}
      <Route path="/login" element={<Navigate to="/auth/login" replace />} />
      <Route path="/signup" element={<Navigate to="/auth/signup" replace />} />

      {/* Onboarding Routes */}
      <Route path="/onboarding" element={
        <OnboardingRoute>
          <SelectIntentPage />
        </OnboardingRoute>
      } />
      <Route path="/onboarding/setup-github" element={
        <OnboardingRoute>
          <SetupGithubPage />
        </OnboardingRoute>
      } />

      {/* Protected Dashboard Routes */}
      <Route path="/projects" element={
        <ProtectedRoute>
          <ProjectsPage />
        </ProtectedRoute>
      } />
      
      {/* Handle legacy /project/skip route - redirect to /projects */}
      <Route path="/project/skip" element={<Navigate to="/projects" replace />} />
      
      <Route path="/project/:projectId/servers" element={
        <ProtectedRoute>
          <ProjectRoute>
            <ServersPage />
          </ProjectRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/project/:projectId/deployments" element={
        <ProtectedRoute>
          <ProjectRoute>
            <DeploymentsPage />
          </ProjectRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/project/:projectId/backups" element={
        <ProtectedRoute>
          <ProjectRoute>
            <BackupsPage />
          </ProjectRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/project/:projectId/tasks" element={
        <ProtectedRoute>
          <ProjectRoute>
            <TasksPage />
          </ProjectRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/project/:projectId/tasks/:taskId" element={
        <ProtectedRoute>
          <ProjectRoute>
            <TaskDetailPage />
          </ProjectRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/project/:projectId/audit-logs" element={
        <ProtectedRoute>
          <ProjectRoute>
            <AuditLogsPage />
          </ProjectRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/project/:projectId/settings" element={
        <ProtectedRoute>
          <ProjectRoute>
            <ProjectSettingsPage />
          </ProjectRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/project/:projectId/ai" element={
        <ProtectedRoute>
          <CodeServerRoute>
            <ProjectRoute>
              <DoployAiPage />
            </ProjectRoute>
          </CodeServerRoute>
        </ProtectedRoute>
      } />

      <Route path="/project/:projectId/ai/knowledge" element={
        <ProtectedRoute>
          <CodeServerRoute>
            <ProjectRoute>
              <KnowledgeBasePage />
            </ProjectRoute>
          </CodeServerRoute>
        </ProtectedRoute>
      } />

      {/* Insights Routes */}
      <Route path="/project/:projectId/insights" element={
        <ProtectedRoute>
          <CodeServerRoute>
            <ProjectRoute>
              <InsightsDashboard />
            </ProjectRoute>
          </CodeServerRoute>
        </ProtectedRoute>
      } />
      <Route path="/project/:projectId/insights/calls/:callId" element={
        <ProtectedRoute>
          <CodeServerRoute>
            <ProjectRoute>
              <CallDetailPage />
            </ProjectRoute>
          </CodeServerRoute>
        </ProtectedRoute>
      } />
      <Route path="/project/:projectId/insights/customers" element={
        <ProtectedRoute>
          <CodeServerRoute>
            <ProjectRoute>
              <CustomerDashboard />
            </ProjectRoute>
          </CodeServerRoute>
        </ProtectedRoute>
      } />

      <Route path="/project/:projectId/*" element={
        <ProtectedRoute>
          <ProjectRoute>
            <ServersPage />
          </ProjectRoute>
        </ProtectedRoute>
      } />
      
      {/* Settings Routes */}
      <Route path="/settings" element={<Navigate to="/settings/account" replace />} />
      <Route path="/settings/account" element={
        <ProtectedRoute>
          <AccountSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/settings/password" element={
        <ProtectedRoute>
          <ChangePasswordPage />
        </ProtectedRoute>
      } />
      <Route path="/settings/2fa" element={
        <ProtectedRoute>
          <TwoFactorPage />
        </ProtectedRoute>
      } />
      <Route path="/settings/oauth" element={
        <ProtectedRoute>
          <LinkedAccountsPage />
        </ProtectedRoute>
      } />
      <Route path="/settings/memberships" element={
        <ProtectedRoute>
          <MembershipsPage />
        </ProtectedRoute>
      } />
      <Route path="/settings/github" element={
        <ProtectedRoute>
          <GitHubSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/settings/ssh-keys" element={
        <ProtectedRoute>
          <SSHKeysPage />
        </ProtectedRoute>
      } />
      <Route path="/settings/billing" element={
        <ProtectedRoute>
          <BillingSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/settings/api" element={
        <ProtectedRoute>
          <ApiAccessPage />
        </ProtectedRoute>
      } />
      <Route path="/settings/legal" element={
        <ProtectedRoute>
          <LegalSettingsPage />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <AdminRoute>
          <AdminDashboardPage />
        </AdminRoute>
      } />
      <Route path="/admin/users" element={
        <AdminRoute>
          <AdminUsersPage />
        </AdminRoute>
      } />
      <Route path="/admin/shared-servers" element={
        <AdminRoute>
          <SharedServersPage />
        </AdminRoute>
      } />
      <Route path="/admin/shared-servers/queue" element={
        <AdminRoute>
          <SharedServerQueuePage />
        </AdminRoute>
      } />
      <Route path="/admin/shared-servers/:serverId" element={
        <AdminRoute>
          <SharedServerDetailPage />
        </AdminRoute>
      } />
      <Route path="/admin/tasks" element={
        <AdminRoute>
          <AdminTasksPage />
        </AdminRoute>
      } />
      <Route path="/admin/tasks/:taskId" element={
        <AdminRoute>
          <AdminTaskDetailPage />
        </AdminRoute>
      } />
      <Route path="/admin/b2" element={
        <AdminRoute>
          <AdminB2Page />
        </AdminRoute>
      } />
      <Route path="/admin/test-tags" element={
        <AdminRoute>
          <AdminTestTagsPage />
        </AdminRoute>
      } />
      <Route path="/admin/odoo-versions" element={
        <AdminRoute>
          <AdminOdooVersionsPage />
        </AdminRoute>
      } />
      <Route path="/admin/continuous-testing" element={
        <AdminRoute>
          <AdminContinuousTestingPage />
        </AdminRoute>
      } />
      <Route path="/admin/email-tools" element={
        <AdminRoute>
          <AdminEmailToolsPage />
        </AdminRoute>
      } />
      <Route path="/admin/project-tools" element={
        <AdminRoute>
          <AdminProjectToolsPage />
        </AdminRoute>
      } />
      <Route path="/admin/playbook-runner" element={
        <AdminRoute>
          <AdminPlaybookRunnerPage />
        </AdminRoute>
      } />
      
      {/* Default - redirect to projects if logged in, login if not */}
      <Route path="/" element={<Navigate to="/projects" replace />} />
      
      {/* 404 Not Found */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
